import axios from "axios";
import store from ".";
import { zonesInfoCollection } from "./fireStore";
import { Shape, Zone } from "../typings/interface";
import firebase from "firebase/app";
import "firebase/auth";

const PROXY_URL = "https://us-central1-miele-shyftbase.cloudfunctions.net/MAPPING-mongoDbApi"

// Token storage key for session storage
const TOKEN_STORAGE_KEY = "firebase_id_token";
const TOKEN_EXPIRY_KEY = "firebase_token_expiry";

/**
 * Token management utilities for session storage
 */
class TokenManager {
  /**
   * Get cached token from session storage
   * @returns {string | null} Cached token or null if not found/expired
   */
  static getCachedToken(): string | null {
    try {
      const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);

      if (!token || !expiry) {
        return null;
      }

      // Check if token is expired (with 5 minute buffer)
      const now = Date.now();
      const expiryTime = parseInt(expiry, 10);
      const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds

      if (now >= expiryTime - bufferTime) {
        // Token is expired or will expire soon, clear it
        this.clearCachedToken();
        return null;
      }

      return token;
    } catch (error) {
      console.error("Error getting cached token:", error);
      return null;
    }
  }

  /**
   * Cache token in session storage with expiry time
   * @param {string} token - Firebase ID token
   * @param {number} expiresIn - Token expiry time in seconds
   */
  static cacheToken(token: string, expiresIn: number): void {
    try {
      const expiryTime = Date.now() + expiresIn * 1000;
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      console.error("Error caching token:", error);
    }
  }

  /**
   * Clear cached token from session storage
   */
  static clearCachedToken(): void {
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error("Error clearing cached token:", error);
    }
  }

  /**
   * Get fresh token from Firebase Auth
   * @returns {Promise<string>} Fresh Firebase ID token
   */
  static async getFreshToken(): Promise<string> {
    const user = firebase.auth().currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }

    // Force refresh to get a new token
    const token = await user.getIdToken(true);
    return token;
  }

  /**
   * Handle token refresh with retry logic
   * @returns {Promise<string>} Valid token
   */
  static async getValidToken(): Promise<string> {
    // Try cached token first
    let token = this.getCachedToken();

    if (token) {
      return token;
    }

    // If no cached token, get fresh one
    try {
      token = await this.getFreshToken();
      this.cacheToken(token, 3600); // Cache for 1 hour
      return token;
    } catch (error) {
      console.error("Failed to get fresh token:", error);
      // Clear any invalid cached data
      this.clearCachedToken();
      throw new Error("Unable to obtain valid authentication token");
    }
  }
}

/**
 * Call MongoDB proxy with cached token management
 * @param {string} action - MongoDB action to perform
 * @param {any} payload - Payload for the action
 * @returns {Promise<any>} Response data from the proxy
 */
async function callMongoProxy(action: string, payload: any) {
  // const user = firebase.auth().currentUser;
  // if (!user) {
  //   throw new Error("User not authenticated");
  // }

  // Get valid token (cached or fresh)
  const token = "" // await TokenManager.getValidToken();

  const res = await axios.post(
    PROXY_URL,
    {
      action,
      ...payload,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return res.data;
}

/**
 * Get database name based on environment
 * @param {string} env - Environment name
 * @returns {string} Database name for the environment
 */
const getDbName = (env: string) => {
  const envMap: Record<string, string> = {
    installation: process.env.VUE_APP_INSTALL_DB_ENV,
    logistics: process.env.VUE_APP_LOGISTICS_DB_ENV,
    services: process.env.VUE_APP_SERVICE_DB_ENV,
    mieleprofessionals: process.env.VUE_APP_PROFESSIONAL_DB_ENV,
  };
  return envMap[env.toLowerCase()] || process.env.VUE_APP_LOGISTICS_DB_ENV;
};

/**
 * Global database name resolution function
 * Centralizes the logic for determining the correct database name based on collection and environment
 * @param {string} collection - Collection name
 * @param {string} environment - Environment name
 * @param {string} [dbOverride] - Optional database override
 * @returns {string} Resolved database name
 */
const getDatabaseName = (
  collection: string,
  environment: string,
  dbOverride?: string
): string => {
  // If dbOverride is provided, use it
  if (dbOverride) {
    return dbOverride;
  }

  // Special case: postal_codes collection always uses logistics database
  if (collection === "postal_codes") {
    return "logistics";
  }

  // For all other collections, use the environment-based database name
  return getDbName(environment);
};

/**
 * Find documents in a collection
 * @param {string} collection - Collection name
 * @param {object} filter - Filter criteria
 * @param {string} [dbOverride] - Optional database override
 * @returns {Promise<any>} Query results
 */
async function find(collection: string, filter = {}, dbOverride?: string) {
  const db = getDatabaseName(collection, store.state.environment, dbOverride);
  console.log("find called in mongo store", collection, filter, dbOverride, db);
  const result = await callMongoProxy("find", {
    database: db,
    collection,
    filter,
  });
  return result;
}

/**
 * Find a single document in a collection
 * @param {string} collection - Collection name
 * @param {object} filter - Filter criteria
 * @param {string} [dbOverride] - Optional database override
 * @returns {Promise<any>} Single document result
 */
export async function findOne(
  collection: string,
  filter = {},
  dbOverride?: string
) {
  const db = getDatabaseName(collection, store.state.environment, dbOverride);
  const result = await callMongoProxy("findOne", {
    database: db,
    collection,
    filter,
  });
  return result;
}

/**
 * Insert a single document into a collection
 * @param {string} collection - Collection name
 * @param {any} document - Document to insert
 * @param {string} [dbOverride] - Optional database override
 * @returns {Promise<string>} Inserted document ID
 */
async function insertOne(
  collection: string,
  document: any,
  dbOverride?: string
) {
  const db = getDatabaseName(collection, store.state.environment, dbOverride);
  const result = await callMongoProxy("insertOne", {
    database: db,
    collection,
    document,
  });
  return result.insertedId;
}

/**
 * Delete a single document from a collection
 * @param {string} collection - Collection name
 * @param {any} filter - Filter criteria for document to delete
 * @param {string} [dbOverride] - Optional database override
 * @returns {Promise<any>} Delete operation result
 */
async function deleteOne(collection: string, filter: any, dbOverride?: string) {
  const db = getDatabaseName(collection, store.state.environment, dbOverride);
  return callMongoProxy("deleteOne", {
    database: db,
    collection,
    filter,
  });
}

/**
 * Delete multiple documents from a collection
 * @param {string} collection - Collection name
 * @param {any} filter - Filter criteria for documents to delete
 * @param {string} [dbOverride] - Optional database override
 * @returns {Promise<any>} Delete operation result
 */
async function deleteMany(
  collection: string,
  filter: any,
  dbOverride?: string
) {
  const db = getDatabaseName(collection, store.state.environment, dbOverride);
  return callMongoProxy("deleteMany", {
    database: db,
    collection,
    filter,
  });
}

/**
 * Update a single document in a collection
 * @param {string} collection - Collection name
 * @param {any} filter - Filter criteria for document to update
 * @param {any} update - Update operations
 * @param {string} [dbOverride] - Optional database override
 * @returns {Promise<any>} Update operation result
 */
async function updateOne(
  collection: string,
  filter: any,
  update: any,
  dbOverride?: string
) {
  const db = getDatabaseName(collection, store.state.environment, dbOverride);
  return callMongoProxy("updateOne", {
    database: db,
    collection,
    filter,
    update,
  });
}

export async function getShapes(shapesID: string) {
  return find("zones", { shapesID });
}

export async function getZones(region: string) {
  const [zoneDocs, shapes] = await Promise.all([
    zonesInfoCollection.get(),
    find("zones", { region }),
  ]);

  const shapesMapped = shapes.map((zone: any) => ({
    ...zone,
    _id: zone._id,
    shapeId: zone._id,
  }));

  const zones = zoneDocs.docs.map((doc) => ({
    shapesID: doc.id.trim(),
    zoneId: doc.id.trim(),
    ...doc.data(),
  }));

  return shapesMapped.map((shape: Shape) => {
    const zoneMatch: Zone | undefined = zones.find(
      (z) => z.shapesID === shape.shapesID
    );
    return {
      ...zoneMatch,
      ...shape,
      zone: zoneMatch?.value,
      zoneColor: zoneMatch?.color,
    };
  });
}

export async function insertZone(
  coordinates: [any],
  shapesID: string,
  multi: boolean,
  fsa?: [string],
  distributionZoneId?: string,
  type?: string,
  daysOfService?: any,
  color?: string
) {
  const region = store.state.region;
  const doc = {
    coordinates: JSON.stringify(coordinates),
    shapesID: shapesID,
    multi: multi,
    fsa: fsa || [],
    region,
    distributionZoneId: distributionZoneId || "",
    type: type || "geofence",
    daysOfService: daysOfService || {},
    color: color || null,
  };

  try {
    const result = await insertOne("zones", doc);
    return {
      coordinates,
      shapesID,
      multi,
      insertedId: result?.insertedId,
    };
  } catch (error) {
    console.error("Error inserting zone:", error);
    throw error;
  }
}

/**
 * Update postal code information
 * @param {any} coordinates - Postal code coordinates
 * @param {string} zoneId - Zone ID
 * @param {boolean} isMulti - Whether it's a multi-zone
 * @param {string} distributionZoneId - Distribution zone ID
 * @param {string[]} zoneClients - Zone clients
 * @param {string[]} originalZoneClients - Original zone clients
 * @param {any} [daysOfService] - Days of service
 * @param {string} [type] - Zone type
 * @param {string} [color] - Zone color
 * @returns {Promise<boolean>} Success status
 */
export async function updatePostalCode(
  coordinates: any,
  zoneId: string,
  isMulti: boolean,
  distributionZoneId: string,
  zoneClients: string[],
  originalZoneClients: string[],
  daysOfService?: any,
  type?: string,
  color?: string
) {
  const environment = store.state.environment.toLowerCase();
  const dbName = getDatabaseName("postal_codes", environment);

  const client: string = process.env.VUE_APP_SHYFTBASE_CUSTOMER_ID;
  try {
    return await axios
      .post(process.env.VUE_APP_UPDATE_POSTAL_CODES,
        {
          coordinates:
            typeof coordinates === "string"
              ? JSON.parse(coordinates)
              : coordinates,
          isMulti: isMulti,
          newZone: zoneId,
          distributionZoneId: distributionZoneId || "",
          dbName,
          client,
          zoneClients: zoneClients || [],
          originalZoneClients: originalZoneClients || [],
          daysOfService: daysOfService || {},
          type: type || "geofence", // Add type field with default
          color: color || null,
        }
      )
      .then(() => {
        return true;
      });
  } catch (error) {
    return false;
  }
}

export async function editZoneCoordinates(data: {
  coordinates: string;
  shapeId: string;
  multi: boolean;
  fsa?: [string];
}) {
  try {
    const result = await updateOne(
      "zones",
      { _id: data.shapeId },
      {
        $set: {
          coordinates: JSON.stringify(data.coordinates),
          multi: data.multi,
          fsa: data.fsa || [],
        },
      }
    );

    return result;
  } catch (error) {
    console.error("Error updating zone coordinates:", error);
    throw error;
  }
}

export const deleteHistory = (id: string) => {
  console.log("not working now", id);
  return null;
}

export async function deleteZone(shapeId: string) {
  console.log("delete zone called in mongo store");
  try {
    // Get the zone document before deleting for sync
    const result = await deleteOne("zones", { _id: shapeId });

    return {
      coordinates: result.coordinates,
      isMulti: result.multi,
      newZone: result.type === "distance" ? result.shapesID : "",
      distributionZoneId: result.distributionZoneId || "",
      zoneClients: result.zoneClients || [],
      type: result.type || "geofence",
    };
  } catch (error) {
    console.error("Error deleting zone:", error);
    throw error;
  }
}

export async function getFSA(region: string) {
  console.log("getFSA called in mongo store");
  const regionMap: Record<string, string> = {
    BC: "British_Columbia",
    AB: "Alberta",
    SK: "Saskatchewan",
    MB: "Manitoba",
    ON: "Ontario",
    QC: "Quebec",
    NB: "New_Brunswick",
    NS: "Nova_Scotia",
    PE: "Prince_Edward_Island",
    NL: "Newfoundland_and_Labrador",
    YT: "Yukon",
    NT: "Northwest_Territories",
    NU: "Nunavut",
  };
  const collection = regionMap[region] || "Ontario";
  return find(collection, {}, "FSA");
}

export async function addHistory(history: any, payload: any) {
  // TODO: Add history to Firestore? do we need a realtime sync?
  console.log("history: ", history);
}

/**
 * @param  {string} newBillingZoneId - New Zone Id to be assign to the shape
 * @param  {string} shapeId - Selected Shape mongodb ID
 */
export async function changeZoneId(
  newBillingZoneId: string,
  shapeId: string,
  distributionZoneId: string,
  calledUndo = false,
  type?: string,
  daysOfService?: any,
  color?: string,
  zoneClients?: string[],
  originalZoneClients?: string[]
) {
  try {
    const {
      shapesID: currentShapesId,
      coordinates: currentCoordinates,
      multi: currentZoneMulti,
      type: currentType,
      daysOfService: currentDaysOfService,
      color: currentColor,
    } = await findOne("zones", {
      _id: shapeId,
    });

    // Preserve existing type, daysOfService, color, or use new values if provided
    const updatedType = type || currentType || "geofence";
    const updatedDaysOfService = daysOfService || currentDaysOfService || {};
    const updatedColor = color || currentColor || null;

    await updateOne(
      "zones",
      {
        _id: shapeId,
      },
      {
        $set: {
          shapesID: newBillingZoneId,
          distributionZoneId: distributionZoneId || "",
          type: updatedType,
          daysOfService: updatedDaysOfService,
          color: updatedColor,
        },
      }
    );

    console.log("updatePostalCode called in changeZoneId");
    // Call updatePostalCode within changeZoneId to ensure it completes even if popup closes
    try {
      await updatePostalCode(
        JSON.parse(currentCoordinates),
        newBillingZoneId,
        currentZoneMulti,
        distributionZoneId,
        zoneClients || [],
        originalZoneClients || [],
        updatedDaysOfService,
        updatedType,
        updatedColor
      );
    } catch (error) {
      // Don't throw error here - we still want to return the zone data even if postal code update fails
    }
    console.log("changeZoneId returned");
    return {
      coordinates: JSON.parse(currentCoordinates),
      newShapesId: newBillingZoneId,
      multi: currentZoneMulti,
      distributionZoneId: distributionZoneId,
      type: updatedType,
      daysOfService: updatedDaysOfService,
      color: updatedColor,
    };
  } catch (e) {
    return false;
  }
}

export async function EditShapeZone(data: {
  coordinates: string;
  shapeId: string;
  multi: boolean;
  fsa: [string];
  calledUndo?: boolean;
}) {
  const shapeId = data.shapeId;
  const newCoordinates = data.coordinates;
  const newMulti = data.multi;
  const newFsa = data.fsa;
  // const calledUndo = data.calledUndo
  // const mongoAppForShape = await mongoDbAppForShape()
  // const zonesCollection = mongoAppForShape.collection('zones')
  // const {
  //   coordinates: currentZoneCoordinates,
  //   multi: currentZoneMulti,
  //   fsa: currentFsa
  // } = await zonesCollection.findOne({
  //   _id: oId
  // })
  // const payloadData = {
  //   zoneShapeId: oId.toString(),
  //   current: {
  //     coordinates: currentZoneCoordinates,
  //     multi: currentZoneMulti,
  //     fsa: currentFsa,
  //   },
  //   new: {
  //     multi: newMulti,
  //     coordinates: JSON.stringify(newCoordinates),
  //     newFsa
  //   }
  // }
  // if (!calledUndo) {
  //   await addHistory(EDIT_COORDINATES_ZONE, payloadData)
  // }

  const originalShape = await findOne("zones", { _id: shapeId });
  const shapesID = originalShape.shapesID;

  await editZoneCoordinates({
    coordinates: newCoordinates,
    shapeId,
    multi: newMulti,
    fsa: newFsa,
  });

  return {
    currentZoneCoordinates: originalShape.coordinates,
    currentZoneMulti: originalShape.multi,
    currentZoneFsa: originalShape.fsa,
    newZoneCoordinates: newCoordinates,
    newMulti,
    newShapesId: shapesID,
  };
  // await editZoneFSA({
  //   shapeId,
  //   fsa: newFsa
  // })
}

export async function getPostalCode(postalcode: string) {
  const postalCodeCapital = postalcode.toUpperCase();

  const postalCodeToSearch =
    postalCodeCapital.substr(0, 3) + " " + postalCodeCapital.substr(3);
  // console.log('postalCodeToSearch', postalCodeToSearch)
  const locations = await find("postal_codes", {
    "properties.Postcode": postalCodeToSearch,
  });
  console.log("locations:", locations);
  return locations;
}

export async function getPostalCodeByRegion(
  region: string,
  environment: string,
  isService: boolean
) {
  // const environment = store.state.environment
  // https://us-central1-geospatial-41457.cloudfunctions.net/MAPPING-getPostalCodeByRegion
  // process.env.VUE_APP_GET_POSTAL_CODES_BY_REGION
  // const region = (store.state.region);
  const client: string = process.env.VUE_APP_SHYFTBASE_CUSTOMER_ID;
  const postalCodesBucket = await axios.post(
    process.env.VUE_APP_GET_POSTAL_CODES_BY_REGION,
    {
      region: region || "",
      client: client || "",
      isService
    }
  );
  const postalCodesBucketUrl = postalCodesBucket.data.result;
  const convertPostalCodeToBinaryFile = await axios.post(
    isService
      ? process.env.VUE_APP_CONVERT_BINARY_SERVICE
      : process.env.VUE_APP_CONVERT_BINARY,
    {
      url: postalCodesBucketUrl,
      client: client || "",
      environment
    }
  );
  const binaryFileBucketUrl = convertPostalCodeToBinaryFile.data.result;
  /* Downloading the results from the storage url and passing to client */
  const binaryFile = await axios.get(binaryFileBucketUrl);
  return binaryFile;
}

/**
 * Clear cached authentication token
 * Call this when user logs out or when token becomes invalid
 */
export function clearAuthToken(): void {
  TokenManager.clearCachedToken();
}

export { deleteMany, find, updateOne, deleteOne, insertOne };
